import React, { useState, useEffect } from 'react';
import awsLogo from '../assets/aws.png';
import ethereumLogo from '../assets/ethereum.webp';
import zustandLogo from '../assets/zustand.png';
import uiPathLogo from '../assets/ui-path.jpg';
import webAudioLogo from '../assets/webaudioapi.png';

// Helper function to get logo URL for tech stack - using colored devicons
const getTechLogo = (techName) => {
  const logoMap = {
    'React': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg',
    'TypeScript': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg',
    'JavaScript': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg',
    'Node.js': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg',
    'Next.js': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg',
    'Redux': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redux/redux-original.svg',
    'TailwindCSS': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tailwindcss/tailwindcss-original.svg',
    'AWS': awsLogo,
    'Docker': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg',
    'MongoDB': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg',
    'PostgreSQL': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg',
    'Python': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg',
    'Solidity': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/solidity/solidity-original.svg',
    'Web3.js': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/web3js/web3js-original.svg',
    'Ethereum': ethereumLogo,
    'IPFS': 'https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/ipfs.svg',
    'TensorFlow.js': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tensorflow/tensorflow-original.svg',
    'Zustand': zustandLogo,
    'Appian': 'https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/appian.svg',
    'Alteryx': 'https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/alteryx.svg',
    'SQL': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg',
    'REST APIs': 'https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/rest.svg',
    'UiPath': uiPathLogo,
    'Twilio': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/twilio/twilio-original.svg',
    'OpenAI GPT': 'https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/openai.svg',
    'Socket.io': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/socketio/socketio-original.svg',
    'Redis': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redis/redis-original.svg',
    'GraphQL': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/graphql/graphql-plain.svg',
    'Web Audio API': webAudioLogo,
    'Express': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg',
    'Firebase': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/firebase/firebase-plain.svg',
    'D3.js': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/d3js/d3js-original.svg',
    'Webpack': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/webpack/webpack-original.svg',
    'Git': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg',
    'GitHub Actions': 'https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/githubactions.svg'
  };
  
  // Handle variations - check specific matches first, then generic ones
  if (techName === 'Web Audio API' || techName.includes('Web Audio')) return logoMap['Web Audio API'];
  if (techName.includes('React')) return logoMap['React'];
  if (techName.includes('TypeScript')) return logoMap['TypeScript'];
  if (techName.includes('JavaScript')) return logoMap['JavaScript'];
  if (techName.includes('Node')) return logoMap['Node.js'];
  if (techName.includes('Next')) return logoMap['Next.js'];
  if (techName.includes('Redux')) return logoMap['Redux'];
  if (techName.includes('Tailwind')) return logoMap['TailwindCSS'];
  if (techName.includes('AWS')) return logoMap['AWS'];
  if (techName.includes('Docker')) return logoMap['Docker'];
  if (techName.includes('Mongo')) return logoMap['MongoDB'];
  if (techName.includes('PostgreSQL') || techName.includes('Postgres')) return logoMap['PostgreSQL'];
  if (techName.includes('Python')) return logoMap['Python'];
  if (techName.includes('GraphQL')) return logoMap['GraphQL'];
  if (techName.includes('Socket')) return logoMap['Socket.io'];
  if (techName.includes('OpenAI') || techName.includes('GPT')) return logoMap['OpenAI GPT'];
  if (techName.includes('Redis')) return logoMap['Redis'];
  if (techName.includes('Express')) return logoMap['Express'];
  if (techName.includes('Firebase')) return logoMap['Firebase'];
  if (techName.includes('D3')) return logoMap['D3.js'];
  if (techName.includes('Webpack')) return logoMap['Webpack'];
  if (techName.includes('Git') && !techName.includes('GitHub')) return logoMap['Git'];
  if (techName.includes('GitHub Actions')) return logoMap['GitHub Actions'];
  if (techName.includes('REST') || techName.includes('API')) return logoMap['REST APIs'];
  if (techName.includes('SQL')) return logoMap['SQL'];
  
  return logoMap[techName] || null;
};

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');
  const [hoveredProject, setHoveredProject] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [animateStats, setAnimateStats] = useState(false);
  const [showMoreProjects, setShowMoreProjects] = useState(false);

  useEffect(() => {
    if (selectedProject) {
      setTimeout(() => setAnimateStats(true), 300);
    } else {
      setAnimateStats(false);
    }
  }, [selectedProject]);

  const projects = [
    {
      id: 6,
      title: 'Noetix-AI',
      subtitle: 'AI-Powered Design System & Repository Intelligence',
      description: 'High-performance intelligence platform solving developer onboarding challenges for massive repositories (1,000+ files). Built with Next.js, D3.js for interactive dependency graphs, and AI for code comprehension. Challenge: Rendering 100,000+ nodes with optimal performance. Learned: Graph visualization, AI integration, and performance optimization at scale.',
      longDescription: 'Noetix-AI acts as a "Google Wiki" for codebases, solving the problem of developer onboarding in massive repositories. Traditional documentation becomes outdated, and understanding file dependencies in large-scale Micro-frontend (MFE) or monolithic architectures is mentally taxing. The platform uses AI to parse repository structures and D3.js to generate interactive, multi-dimensional dependency graphs, allowing developers to see—not just read—how their code is connected.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop',
      icon: '🧠',
      color: '#0A84FF',
      gradient: 'linear-gradient(135deg, #0A84FF 0%, #007AFF 100%)',
      techStack: ['Next.js', 'TypeScript', 'D3.js', 'TailwindCSS', 'Node.js', 'Webpack', 'Docker', 'Git', 'Python', 'OpenAI GPT', 'AWS', 'GitHub Actions'],
      category: 'AI/ML',
      status: 'Production',
      year: '2026',
      featured: true,
      metrics: [
        { value: '10K+', label: 'Nodes Rendered' },
        { value: '5min', label: 'Comprehension Time' },
        { value: '20x', label: 'Faster Onboarding' }
      ],
      features: [
        'Interactive D3.js dependency graphs with 100,000+ nodes',
        'AI-powered code summaries and documentation',
        'Human-readable SQL schema explanations',
        'Multi-dimensional architecture visualization',
        'Webpack chunking for optimized rendering',
        'Global-ready deployment with AWS edge locations'
      ],
      challenges: 'Optimizing the rendering engine to handle 100,000+ nodes using Webpack chunking and memoization while maintaining interactive performance.',
      outcome: 'Reduced code comprehension time from 36 hours to under 5 minutes, revolutionizing developer onboarding.'
    },
    {
      id: 2,
      title: 'AI DJ Music Application',
      subtitle: 'Intelligent Music Experience',
      description: 'AI DJ app solving the problem of manual music mixing complexity. Built with Web Audio API, TensorFlow.js for beat detection, and real-time audio processing. Challenge: Achieving sub-10ms latency with ML models. Learned: Audio signal processing, real-time performance optimization, and ML integration in browsers.',
      longDescription: 'Revolutionary music application solving manual mixing complexity. Combines AI with professional DJ capabilities, analyzing tracks in real-time and applying intelligent effects. Key challenge was maintaining sub-10ms latency while running ML models for beat detection in the browser.',
      image: 'https://images.unsplash.com/photo-1571330735066-03aaa9429d89?w=600&h=400&fit=crop',
      icon: '🎧',
      color: '#0A84FF',
      gradient: 'linear-gradient(135deg, #0A84FF 0%, #007AFF 100%)',
      techStack: ['React', 'Web Audio API', 'TensorFlow.js', 'Python', 'Zustand', 'Node.js'],
      category: 'AI/ML',
      status: 'Live',
      year: '2025',
      featured: true,
      metrics: [
        { value: '10+', label: 'Audio Effects' },
        { value: '< 10ms', label: 'Latency' },
        { value: '4.8★', label: 'User Rating' }
      ],
      features: [
        'Real-time audio transformations (Lo-Fi, EDM, Nightcore)',
        'AI-powered beat matching and sync',
        '3-band EQ with professional filters',
        'Intelligent music recommendations',
        'Custom DJ sound effects library',
        'Cross-fade and transition automation'
      ],
      challenges: 'Achieving sub-10ms audio processing latency while running ML models for beat detection.',
      outcome: 'Created an industry-leading audio experience with studio-quality effects in the browser.'
    },
    {
      id: 5,
      title: 'Real-Time Chatbot Application',
      subtitle: 'Conversational AI Platform',
      description: 'Chatbot solving customer support scalability issues. Built with OpenAI GPT, Socket.io for real-time communication, and Redis for context management. Challenge: Maintaining conversation context across thousands of concurrent users. Learned: NLP integration, real-time systems, and scalable state management.',
      longDescription: 'Enterprise-grade chatbot solving customer support scalability. Leverages GPT-powered NLP for human-like conversations, maintains context across sessions, and escalates complex queries. Key challenge was building a system that handles thousands of concurrent users while preserving conversation context.',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop',
      icon: '🤖',
      color: '#0A84FF',
      gradient: 'linear-gradient(135deg, #0A84FF 0%, #007AFF 100%)',
      techStack: ['React', 'OpenAI GPT', 'Socket.io', 'Node.js', 'MongoDB', 'Redis'],
      category: 'AI/ML',
      status: 'Production',
      year: '2025',
      metrics: [
        { value: '100K+', label: 'Conversations' },
        { value: '94%', label: 'Resolution Rate' },
        { value: '12', label: 'Languages' }
      ],
      features: [
        'GPT-powered natural language understanding',
        'Real-time sentiment analysis',
        'Multi-language support with auto-detection',
        'Context-aware conversation memory',
        'Seamless human agent handoff',
        'Analytics dashboard with insights'
      ],
      challenges: 'Building a system that maintains conversation context while handling thousands of concurrent users.',
      outcome: 'Achieved 94% first-contact resolution rate, reducing support costs by 60%.'
    },
    {
      id: 3,
      title: 'Robotic Process Automation',
      subtitle: 'Appian & Alteryx Integration',
      description: 'RPA solution solving manual process inefficiencies. Built with Appian workflows, Alteryx data analytics, and Python automation scripts. Challenge: Integrating legacy systems with modern automation tools. Learned: Low-code platforms, data transformation pipelines, and enterprise automation patterns.',
      longDescription: 'Comprehensive automation platform solving manual process inefficiencies. Combines Appian\'s low-code workflows with Alteryx data transformation for end-to-end automation. Key challenge was integrating legacy systems while ensuring data integrity and maintaining business logic.',
      image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=600&h=400&fit=crop',
      icon: '🤖',
      color: '#0A84FF',
      gradient: 'linear-gradient(135deg, #0A84FF 0%, #007AFF 100%)',
      techStack: ['Appian', 'Alteryx', 'Python', 'SQL', 'REST APIs', 'UiPath'],
      category: 'Automation',
      status: 'Enterprise',
      year: '2024',
      metrics: [
        { value: '85%', label: 'Time Saved' },
        { value: '200+', label: 'Processes' },
        { value: '$2M+', label: 'Cost Savings' }
      ],
      features: [
        'Appian low-code workflow automation',
        'Alteryx data blending and analytics',
        'Intelligent document processing',
        'Exception handling with human-in-loop',
        'Real-time process monitoring dashboard',
        'Scheduled and triggered automation'
      ],
      challenges: 'Integrating legacy systems with modern automation tools while ensuring data integrity.',
      outcome: 'Automated 200+ business processes, saving over 10,000 manual hours annually.'
    },
    {
      id: 1,
      title: 'Blockchain Technology Platform',
      subtitle: 'Decentralized Finance Solution',
      description: 'Blockchain platform solving trust and transparency issues in financial transactions. Built with Solidity smart contracts, Web3.js integration, and secure wallet management. Challenge: Ensuring gas optimization while maintaining security. Learned: Blockchain architecture, consensus mechanisms, and decentralized system design.',
      longDescription: 'Built a robust blockchain infrastructure solving trust issues in financial transactions. The platform includes smart contract deployment, secure wallet management, and real-time transaction tracking. Key challenge was optimizing gas costs while maintaining security across multiple blockchain networks.',
      image: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=600&h=400&fit=crop',
      icon: '⛓️',
      color: '#0A84FF',
      gradient: 'linear-gradient(135deg, #0A84FF 0%, #007AFF 100%)',
      techStack: ['Solidity', 'Web3.js', 'React', 'Node.js', 'Ethereum', 'IPFS'],
      category: 'Blockchain',
      status: 'Production',
      year: '2024',
      metrics: [
        { value: '50K+', label: 'Transactions' },
        { value: '99.9%', label: 'Uptime' },
        { value: '<2s', label: 'Tx Speed' }
      ],
      features: [
        'Smart contract development and deployment',
        'Multi-wallet integration (MetaMask, WalletConnect)',
        'Real-time blockchain transaction monitoring',
        'Decentralized storage with IPFS',
        'Gas optimization for cost-effective transactions',
        'Cross-chain bridge implementation'
      ],
      challenges: 'Implementing efficient gas optimization while maintaining security standards across multiple blockchain networks.',
      outcome: 'Successfully processed over 50,000 transactions with zero security incidents.'
    },
    {
      id: 4,
      title: 'Warranty & Services Application',
      subtitle: 'Customer Service Platform',
      description: 'Warranty system solving fragmented service management. Built with React, Node.js, PostgreSQL, and AWS infrastructure. Challenge: Creating unified platform for customers, technicians, and admins. Learned: Multi-tenant architecture, real-time updates, and complex workflow automation.',
      longDescription: 'Complete warranty lifecycle platform solving fragmented service management. Streamlines claim submission, validation, and fulfillment across customer portal, technician app, and admin dashboard. Key challenge was building a unified platform serving different user needs while maintaining data consistency.',
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=400&fit=crop',
      icon: '🛡️',
      color: '#0A84FF',
      gradient: 'linear-gradient(135deg, #0A84FF 0%, #007AFF 100%)',
      techStack: ['React', 'Node.js', 'PostgreSQL', 'Redux', 'AWS', 'Twilio'],
      category: 'Enterprise',
      status: 'Production',
      year: '2023',
      metrics: [
        { value: '50K+', label: 'Claims Processed' },
        { value: '24hrs', label: 'Avg Resolution' },
        { value: '98%', label: 'Satisfaction' }
      ],
      features: [
        'Automated warranty claim validation',
        'Real-time service ticket tracking',
        'Customer self-service portal',
        'Technician mobile application',
        'Predictive maintenance alerts',
        'Integration with CRM and ERP systems'
      ],
      challenges: 'Creating a unified platform that serves customers, technicians, and administrators with different needs.',
      outcome: 'Reduced average claim resolution time from 7 days to 24 hours, improving customer satisfaction by 40%.'
    }
  ];

  const categories = ['All', 'AI/ML', 'Blockchain', 'Automation', 'Enterprise'];

  const flagshipProjects = projects.slice(0, 3);
  const moreProjects = projects.slice(3);
  
  const filteredFlagship = activeCategory === 'All'
    ? flagshipProjects
    : flagshipProjects.filter(project => project.category === activeCategory);
  
  const filteredMore = activeCategory === 'All'
    ? moreProjects
    : moreProjects.filter(project => project.category === activeCategory);
  
  const filteredProjects = filteredFlagship;

  const projectStats = {
    total: projects.length,
    technologies: [...new Set(projects.flatMap(p => p.techStack))].length,
    categories: categories.length - 1
  };

  return (
    <section id="projects" className="projects-section-enhanced">
      <div className="projects-container-enhanced">
        {/* Header */}
        <div className="section-header">
          <h2 className="section-title">Projects & Innovations</h2>
          <p className="section-subtitle">
            A showcase of cutting-edge solutions spanning blockchain, AI, automation, and enterprise applications
          </p>
          
          {/* Quick Stats */}
          <div className="projects-quick-stats">
            <div className="quick-stat">
              <span className="stat-number">{projectStats.total}</span>
              <span className="stat-text">Projects</span>
            </div>
            <div className="stat-divider"></div>
            <div className="quick-stat">
              <span className="stat-number">{projectStats.technologies}+</span>
              <span className="stat-text">Technologies</span>
            </div>
            <div className="stat-divider"></div>
            <div className="quick-stat">
              <span className="stat-number">{projectStats.categories}</span>
              <span className="stat-text">Domains</span>
            </div>
          </div>
        </div>

        {/* Filter & View Toggle */}
        <div className="projects-controls">
          <div className="filter-pills">
            {categories.map(category => (
              <button
                key={category}
                className={`filter-pill ${activeCategory === category ? 'active' : ''}`}
                onClick={() => setActiveCategory(category)}
              >
                {category === 'All' && '🌐'}
                {category === 'Blockchain' && '⛓️'}
                {category === 'AI/ML' && '🧠'}
                {category === 'Automation' && '⚙️'}
                {category === 'Enterprise' && '🏢'}
                <span>{category}</span>
              </button>
            ))}
          </div>
          
          <div className="view-toggle">
            <button 
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid View"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <rect x="3" y="3" width="7" height="7" rx="1"/>
                <rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/>
                <rect x="14" y="14" width="7" height="7" rx="1"/>
              </svg>
            </button>
            <button 
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <rect x="3" y="4" width="18" height="4" rx="1"/>
                <rect x="3" y="10" width="18" height="4" rx="1"/>
                <rect x="3" y="16" width="18" height="4" rx="1"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Projects Grid/List */}
        <div className={`projects-showcase ${viewMode}`}>
          {filteredProjects.map((project, index) => (
            <div
              key={project.id}
              className={`project-card-enhanced ${hoveredProject === project.id ? 'hovered' : ''}`}
              style={{ 
                '--project-color': project.color,
                '--project-gradient': project.gradient,
                animationDelay: `${index * 0.1}s`
              }}
              onMouseEnter={() => setHoveredProject(project.id)}
              onMouseLeave={() => setHoveredProject(null)}
              onClick={() => setSelectedProject(project)}
            >
              {/* Card Glow Effect */}
              <div className="card-glow"></div>
              
              {/* Image Section */}
              <div className="card-image-wrapper">
                <img src={project.image} alt={project.title} className="card-image" />
                <div className="image-overlay">
                  <span className="view-project-btn">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M15 3h6v6M14 10l6.1-6.1M9 21H3v-6M10 14l-6.1 6.1"/>
                    </svg>
                    View Details
                  </span>
                </div>
                <div className="card-icon">{project.icon}</div>
                <div className="card-status" style={{ background: project.gradient }}>
                  {project.status}
                </div>
                {project.featured && (
                  <div className="featured-badge">
                    <span className="featured-icon">⭐</span>
                    <span className="featured-text">Featured Repo</span>
                  </div>
                )}
              </div>

              {/* Content Section */}
              <div className="card-content-enhanced">
                <div className="card-meta">
                  <span className="card-category">{project.category}</span>
                  <span className="card-year">{project.year}</span>
                </div>
                
                <h3 className="card-title-enhanced">{project.title}</h3>
                <p className="card-subtitle">{project.subtitle}</p>
                <p className="card-description-enhanced">{project.description}</p>

                {/* Mini Metrics */}
                <div className="card-mini-metrics">
                  {project.metrics.slice(0, 2).map((metric, idx) => (
                    <div key={idx} className="mini-metric">
                      <span className="mini-value" style={{ color: project.color }}>{metric.value}</span>
                      <span className="mini-label">{metric.label}</span>
                    </div>
                  ))}
                </div>

                {/* Tech Stack Preview */}
                <div className="card-tech-preview">
                  {project.techStack.slice(0, 4).map((tech, idx) => {
                    const logoUrl = getTechLogo(tech);
                    return (
                      <span key={idx} className="tech-chip">
                        {logoUrl && (
                          <img src={logoUrl} alt={tech} className="tech-chip-logo" />
                        )}
                        {tech}
                      </span>
                    );
                  })}
                  {project.techStack.length > 4 && (
                    <span className="tech-more">+{project.techStack.length - 4}</span>
                  )}
                </div>
              </div>

              {/* Animated Border */}
              <div className="card-border-glow"></div>
            </div>
          ))}
        </div>

        {/* More Projects Accordion */}
        {moreProjects.length > 0 && (
          <div style={{ marginTop: '40px' }}>
            <button 
              onClick={() => setShowMoreProjects(!showMoreProjects)}
              style={{
                width: '100%',
                padding: '15px 20px',
                background: 'var(--color-card)',
                border: '1px solid rgba(10, 132, 255, 0.2)',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '1.1rem',
                fontWeight: 600,
                color: 'var(--color-heading)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <span>More Projects</span>
              <span>{showMoreProjects ? '−' : '+'}</span>
            </button>
            {showMoreProjects && (
              <div className={`projects-showcase ${viewMode}`} style={{ marginTop: '20px' }}>
                {filteredMore.map((project, index) => (
                  <div
                    key={project.id}
                    className={`project-card-enhanced ${hoveredProject === project.id ? 'hovered' : ''}`}
                    style={{ 
                      '--project-color': project.color,
                      '--project-gradient': project.gradient,
                      animationDelay: `${index * 0.1}s`
                    }}
                    onMouseEnter={() => setHoveredProject(project.id)}
                    onMouseLeave={() => setHoveredProject(null)}
                    onClick={() => setSelectedProject(project)}
                  >
                    <div className="card-glow"></div>
                    <div className="card-image-wrapper">
                      <img src={project.image} alt={project.title} className="card-image" />
                      <div className="image-overlay">
                        <span className="view-project-btn">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 3h6v6M14 10l6.1-6.1M9 21H3v-6M10 14l-6.1 6.1"/>
                          </svg>
                          View Details
                        </span>
                      </div>
                      <div className="card-icon">{project.icon}</div>
                      <div className="card-status" style={{ background: project.gradient }}>
                        {project.status}
                      </div>
                    </div>
                    <div className="card-content-enhanced">
                      <div className="card-meta">
                        <span className="card-category">{project.category}</span>
                        <span className="card-year">{project.year}</span>
                      </div>
                      <h3 className="card-title-enhanced">{project.title}</h3>
                      <p className="card-subtitle">{project.subtitle}</p>
                      <p className="card-description-enhanced">{project.description}</p>
                      <div className="card-mini-metrics">
                        {project.metrics.slice(0, 2).map((metric, idx) => (
                          <div key={idx} className="mini-metric">
                            <span className="mini-value" style={{ color: project.color }}>{metric.value}</span>
                            <span className="mini-label">{metric.label}</span>
                          </div>
                        ))}
                      </div>
                      <div className="card-tech-preview">
                        {project.techStack.slice(0, 4).map((tech, idx) => {
                          const logoUrl = getTechLogo(tech);
                          return (
                            <span key={idx} className="tech-chip">
                              {logoUrl && (
                                <img src={logoUrl} alt={tech} className="tech-chip-logo" />
                              )}
                              {tech}
                            </span>
                          );
                        })}
                        {project.techStack.length > 4 && (
                          <span className="tech-more">+{project.techStack.length - 4}</span>
                        )}
                      </div>
                    </div>
                    <div className="card-border-glow"></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Enhanced Modal */}
      {selectedProject && (
        <div className="project-modal-overlay" onClick={() => setSelectedProject(null)}>
          <div 
            className="project-modal-enhanced" 
            onClick={(e) => e.stopPropagation()}
            style={{ '--modal-color': selectedProject.color, '--modal-gradient': selectedProject.gradient }}
          >
            {/* Modal Header with Image */}
            <div className="modal-header-enhanced">
              <img src={selectedProject.image} alt={selectedProject.title} className="modal-hero-image" />
              <div className="modal-header-overlay"></div>
              <button className="modal-close-enhanced" onClick={() => setSelectedProject(null)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
              <div className="modal-header-content">
                <span className="modal-icon">{selectedProject.icon}</span>
                <div className="modal-header-text">
                  <span className="modal-category-badge" style={{ background: selectedProject.gradient }}>
                    {selectedProject.category}
                  </span>
                  <h2 className="modal-title">{selectedProject.title}</h2>
                  <p className="modal-subtitle-text">{selectedProject.subtitle}</p>
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div className="modal-body-enhanced">
              {/* Metrics Row */}
              <div className="modal-metrics-row">
                {selectedProject.metrics.map((metric, idx) => (
                  <div 
                    key={idx} 
                    className={`modal-metric ${animateStats ? 'animate' : ''}`}
                    style={{ animationDelay: `${idx * 0.15}s` }}
                  >
                    <span className="modal-metric-value" style={{ color: selectedProject.color }}>
                      {metric.value}
                    </span>
                    <span className="modal-metric-label">{metric.label}</span>
                  </div>
                ))}
              </div>

              {/* Description */}
              <div className="modal-section">
                <h4 className="modal-section-title">
                  <span className="section-icon">📋</span> Overview
                </h4>
                <p className="modal-description">{selectedProject.longDescription}</p>
              </div>

              {/* Features */}
              <div className="modal-section">
                <h4 className="modal-section-title">
                  <span className="section-icon">✨</span> Key Features
                </h4>
                <div className="modal-features-grid">
                  {selectedProject.features.map((feature, idx) => (
                    <div key={idx} className="modal-feature-item">
                      <span className="feature-check" style={{ background: selectedProject.gradient }}>✓</span>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tech Stack */}
              <div className="modal-section">
                <h4 className="modal-section-title">
                  <span className="section-icon">🛠️</span> Technology Stack
                </h4>
                <div className="modal-tech-grid">
                  {selectedProject.techStack.map((tech, idx) => {
                    const logoUrl = getTechLogo(tech);
                    return (
                      <span 
                        key={idx} 
                        className="modal-tech-badge"
                        style={{ '--tech-index': idx }}
                      >
                        {logoUrl && (
                          <img src={logoUrl} alt={tech} className="tech-badge-logo" />
                        )}
                        {tech}
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* Challenge & Outcome */}
              <div className="modal-insights-row">
                <div className="modal-insight">
                  <h5>🎯 Challenge</h5>
                  <p>{selectedProject.challenges}</p>
                </div>
                <div className="modal-insight outcome">
                  <h5>🏆 Outcome</h5>
                  <p>{selectedProject.outcome}</p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="modal-footer-enhanced">
              <div className="modal-year-badge">
                <span>📅</span> {selectedProject.year}
              </div>
              <div className="modal-status-badge" style={{ background: selectedProject.gradient }}>
                {selectedProject.status}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Projects;
